import type { CommissionRules } from '@kilat/shared/schemas/commission-rules';

/**
 * Default commission rules seeded into `platformConfig/commissionRules`
 * on first deploy. Source of truth for the type is
 * `@kilat/shared/schemas/commission-rules` (Sprint 80 unified the
 * inline interface that previously lived here).
 *
 * Sprint 80 additions (recruiter epic foundation) all default to 0
 * → existing platform behaves exactly as before. Super-admin opts in
 * via the `/superadmin/plans` editor (Sprint 81).
 */
export type { CommissionRules };

export const defaultCommissionRules: CommissionRules = {
  // Sprint 95 — one-time commission model.
  // Agent: 1 month of the operator's subscription, tier-multiplied.
  // Recruiter: flat RM amount per downstream operator signup,
  // tier-multiplied; default RM 30 to give super-admin a sensible
  // out-of-the-box value (still freely editable).
  signupCommissionMultiplier: 1.0,
  recruiterPerSignupSen: 3_000, // RM 30 per downstream signup
  // ADR-0029 Survival Bonus — ships OFF (multiplier/sen = 0) so behaviour is
  // unchanged until super-admin sets the amount. Threshold pre-set to 90 days.
  // Owner decision (the money value) is deliberately left at 0 here.
  survivalBonusMultiplier: 0,
  survivalBonusThresholdDays: 90,
  recruiterSurvivalBonusSen: 0,
  // ADR-0034 Residual (trailing) commission — the agent's share of every paid
  // invoice AFTER the first, while the operator pays and the agent is active.
  // Ships OFF (0) like every new money layer; super-admin sets the rate.
  recurringRate: 0,
  perExtraOutletRate: 0,
  signupBonus: 0,
  tierMultipliers: { bronze: 1.0, silver: 1.1, gold: 1.25 },
  recruiterTierMultipliers: { bronze: 1.0, silver: 1.1, gold: 1.25 },
  recruiterRecurringRate: 0, // ADR-0034 residual, recruiter side — ships OFF
  firstInvoice: {
    signupAgentSharePct: 0,
    signupRecruiterSharePct: 0,
    activationAgentSharePct: 0,
    activationRecruiterSharePct: 0,
  },
  autoPromotionThresholds: { silverActiveOperators: 5, goldActiveOperators: 15 },
  clawbackWindowDays: 30,
  payoutThreshold: 10_000, // RM 100
  payoutMethod: 'duitnow',
  payoutDayOfMonth: 7,
};
